import { useEffect, useRef, useState } from 'react'
import { supabase, getCurrentSession } from '../../lib/supabase'
import { Button } from '../ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { MemberRoleSelect } from './MemberRoleSelect'
import { saveWorkspaceMemberRole, type EditableWorkspaceRole } from '../../lib/workspaceMemberRoles'
import { MemberPicker, MemberMoveConfirmation } from './MemberPicker'

interface Candidate {
  id: string
  name: string
  email: string | null
  avatar: string | null
  currentPodId: string | null
  currentPodName: string | null
  role: string | null
  hasSignedIn: boolean
  canManageManager?: boolean
  isAlreadyMember: boolean
}
interface Pod { id: string; name: string; archived_at: string | null }

async function requestError(error: unknown, fallback: string): Promise<Error> {
  const response = (error as { context?: Response })?.context
  if (response?.clone) {
    try {
      const body = await response.clone().json()
      if (body?.details === 'ratelimited' || (response.status === 429 && !body?.error)) {
        return new Error('Slack is limiting member requests. Please wait a minute, then refresh membership.')
      }
      if (typeof body?.error === 'string' && body.error !== 'Internal server error') return new Error(body.error)
    } catch { /* A failed gateway response may not contain JSON. */ }
    return new Error(fallback)
  }
  return error instanceof Error && !error.message.includes('non-2xx') ? error : new Error(fallback)
}

export function AddSlackMembersSheet({ teamId, initialPodId, pods, isOwner, onClose, onSaved, workspaceOnly = false, onSavingChange }: {
  teamId: string
  initialPodId: string
  pods: Pod[]
  isOwner: boolean
  onClose: () => void
  onSaved: () => Promise<void>
  workspaceOnly?: boolean
  onSavingChange?: (saving: boolean) => void
}) {
  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [podId, setPodId] = useState(initialPodId)
  const [role, setRole] = useState('member')
  const [inviteRoles, setInviteRoles] = useState<Record<string, EditableWorkspaceRole>>({})
  const [roleUpdatingId, setRoleUpdatingId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [needsRefresh, setNeedsRefresh] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const busy = useRef(false)
  const mounted = useRef(true)
  const pendingCandidates = useRef<Promise<Candidate[]> | null>(null)
  const activePods = pods.filter(pod => !pod.archived_at)
  const target = activePods.find(pod => pod.id === podId)
  const eligible = (candidate: Candidate) => workspaceOnly ? !candidate.isAlreadyMember : candidate.currentPodId !== podId
  const picked = candidates.filter(candidate => selected.has(candidate.id) && eligible(candidate))
  const moves = (workspaceOnly ? [] : picked).filter(candidate => candidate.currentPodId).map(candidate => `${candidate.name} — ${candidate.currentPodName || 'another team'}`)

  useEffect(() => { onSavingChange?.(saving || !!roleUpdatingId); return () => onSavingChange?.(false) }, [saving, roleUpdatingId, onSavingChange])

  function fetchCandidates(): Promise<Candidate[]> {
    if (pendingCandidates.current) return pendingCandidates.current
    pendingCandidates.current = (async () => {
      const session = await getCurrentSession()
      if (!session) throw new Error('No session found')
      const { data, error } = await supabase.functions.invoke('get-slack-users', {
        body: { team_id: teamId }, headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (error) throw await requestError(error, 'Unable to load Slack members. Please refresh membership to try again.')
      if (!Array.isArray(data?.users)) throw new Error(data?.error || 'Unable to load Slack members')
      return data.users as Candidate[]
    })().finally(() => { pendingCandidates.current = null })
    return pendingCandidates.current
  }

  useEffect(() => {
    mounted.current = true
    let active = true
    fetchCandidates().then(users => { if (active) { setCandidates(users); setLoaded(true) } })
      .catch(err => { if (active) { setError(err.message); setNeedsRefresh(true) } })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false; mounted.current = false }
    // This sheet is mounted afresh for each workspace/open session.
  }, [])

  async function reconcile() {
    const fresh = await fetchCandidates()
    if (!mounted.current) return
    setCandidates(fresh)
    setLoaded(true)
    setSelected(current => new Set(fresh.filter(candidate => current.has(candidate.id) && eligible(candidate)).map(candidate => candidate.id)))
    setConfirmed(false)
    setNeedsRefresh(false)
    await onSaved()
  }

  async function save() {
    if (busy.current || (!workspaceOnly && !target) || !picked.length || (moves.length && !confirmed) || needsRefresh) return
    busy.current = true
    setSaving(true)
    setError('')
    try {
      const session = await getCurrentSession()
      if (!session) throw new Error('No session found')
      const { data, error } = await supabase.functions.invoke('invite-team-members', {
        body: { team_id: teamId, ...(workspaceOnly ? {} : { pod_id: podId }), users: picked.map(candidate => ({
          slack_user_id: candidate.id, name: candidate.name, email: candidate.email,
          // Existing users keep their permissions and job titles during assignment.
          ...(candidate.isAlreadyMember ? {} : { role: isOwner ? inviteRoles[candidate.id] || role : 'member' }),
        })) }, headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (error) throw await requestError(error, 'Unable to add members. Please refresh membership before retrying.')
      if (!data?.success) throw new Error(data?.error || 'Unable to add members')
      // Refresh persisted assignment even on partial success; names are not unique
      // and the legacy endpoint identifies individual failures only by name.
      await reconcile()
      if (data.errors?.length) {
        setError(`${data.invited ?? 0} added; ${data.errors.length} failed. ${data.errors.map((item: { user: string; error: string }) => `${item.user}: ${item.error}`).join('; ')}`)
      } else {
        onClose()
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to add members'
      setNeedsRefresh(true)
      try {
        await reconcile()
        setError(`${message}. Membership refreshed; review the remaining selection before retrying.`)
      } catch {
        setError(`${message}. Refresh membership before retrying to avoid adding people twice.`)
      }
    } finally {
      busy.current = false
      if (mounted.current) setSaving(false)
    }
  }

  async function refresh() {
    if (busy.current) return
    busy.current = true
    setLoading(true)
    try { await reconcile(); setError('') }
    catch (error) { setError(error instanceof Error ? error.message : 'Unable to refresh membership. Try again before adding members.'); setNeedsRefresh(true) }
    finally { busy.current = false; if (mounted.current) setLoading(false) }
  }

  async function changeExistingRole(candidate: Candidate, next: EditableWorkspaceRole) {
    if (busy.current) throw new Error('Another change is saving. Please try again.')
    busy.current = true
    setRoleUpdatingId(candidate.id)
    try {
      await saveWorkspaceMemberRole(teamId, { member_slack_user_id: candidate.id }, next)
      setCandidates(current => current.map(item => item.id === candidate.id ? { ...item, role: next } : item))
    } finally {
      busy.current = false
      setRoleUpdatingId(null)
    }
  }

  const content = <>
      {!workspaceOnly && <SheetHeader className="border-b px-6 py-6"><SheetTitle>Add Slack members</SheetTitle></SheetHeader>}
      <div className={workspaceOnly ? "space-y-5" : "min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-6"}>
        {workspaceOnly ? <p className="text-sm text-muted-foreground">Select the people to add. You can organize them into teams later.</p> : <SheetDescription>Choose people from your connected Slack workspace. Nothing changes until you add them.</SheetDescription>}
        {!workspaceOnly && <label className="block space-y-2"><span className="text-sm font-medium">Team</span>
          <Select value={podId} disabled={saving || loading} onValueChange={value => { setPodId(value); setSelected(new Set()); setConfirmed(false) }}>
            <SelectTrigger aria-label="Team"><SelectValue /></SelectTrigger>
            <SelectContent>{activePods.map(pod => <SelectItem key={pod.id} value={pod.id}>{pod.name}</SelectItem>)}</SelectContent>
          </Select>
        </label>}
        {loading && <p role="status">Loading Slack members…</p>}
        {loaded && <MemberPicker options={candidates.map(candidate => ({
          id: candidate.id, name: candidate.name, email: candidate.email, avatar: candidate.avatar,
          disabled: !eligible(candidate),
          description: [!eligible(candidate) ? workspaceOnly ? 'Already a member' : 'Already in team' : candidate.currentPodName ? `Currently in ${candidate.currentPodName}` : candidate.email,
            candidate.role ? `${candidate.role.charAt(0).toUpperCase()}${candidate.role.slice(1)}` : '',
            candidate.isAlreadyMember && candidate.hasSignedIn === false ? 'Pending sign-in' : ''].filter(Boolean).join(' · '),
        }))} selected={selected} disabled={saving || !!roleUpdatingId || loading || needsRefresh} onChange={ids => { setSelected(ids); setConfirmed(false) }}
          renderAction={workspaceOnly ? option => {
            const candidate = candidates.find(item => item.id === option.id)!
            return <MemberRoleSelect name={candidate.name} role={candidate.isAlreadyMember ? candidate.role || 'member' : inviteRoles[candidate.id] || 'member'}
              actorRole={isOwner ? 'owner' : 'admin'} managerAvailable={candidate.canManageManager === true}
              disabled={saving || !!roleUpdatingId || loading || needsRefresh}
              onChange={next => candidate.isAlreadyMember ? changeExistingRole(candidate, next) : setInviteRoles(current => ({ ...current, [candidate.id]: next }))} />
          } : undefined} />}
        {isOwner && !workspaceOnly && <label className="block space-y-2"><span className="text-sm font-medium">Permission for new members</span>
          <Select value={role} disabled={saving} onValueChange={setRole}>
            <SelectTrigger aria-label="Permission for new members"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="member">Member</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
          </Select>
        </label>}
        <p className="text-xs text-muted-foreground">{workspaceOnly ? 'Existing member role changes save immediately. New member roles save when you add them. Ownership changes use the ownership transfer flow.' : 'Existing members keep their permissions. Adding someone does not grant workspace ownership.'}</p>
        <MemberMoveConfirmation moves={moves} confirmed={confirmed} onChange={setConfirmed} disabled={saving} />
        {error && <p role="alert" className="break-words text-sm text-destructive">{error}</p>}
        {needsRefresh && <Button variant="outline" disabled={saving || loading} onClick={refresh}>Refresh membership</Button>}
      </div>
      <div className={workspaceOnly ? "flex flex-wrap justify-end gap-2 pt-4" : "flex justify-between gap-2 border-t px-6 py-5"}>
        {!workspaceOnly && <Button variant="outline" disabled={saving} onClick={onClose}>Cancel</Button>}
        <Button disabled={saving || !!roleUpdatingId || loading || needsRefresh || (!workspaceOnly && !target) || !picked.length || (!!moves.length && !confirmed)} onClick={save}>{saving ? 'Saving…' : `Add ${picked.length} ${picked.length === 1 ? 'member' : 'members'}`}</Button>
      </div>
    </>
  if (workspaceOnly) return <div aria-label="Add workspace members">{content}</div>
  return <Sheet open onOpenChange={open => { if (!open && !saving) onClose() }}>
    <SheetContent className="flex h-full w-full flex-col gap-0 p-0 sm:max-w-lg">{content}</SheetContent>
  </Sheet>
}
